import { useEffect, useState } from 'react';
import {
  Alert,
  AlertColor,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { getJwtTokenFromIdToken } from '../auth/AuthFanc';
import UpdateButton from '../../atoms/Button/UpdateButton';
import {
  CustomContainer,
  StyledDataGrid,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
} from '../../../styles/styles';
import VisitorAppRegistrationModal from './VisitorAppRegistrationModal';
import ConfirmDialog from '../../molecules/ConfirmDialog';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import QRCode from 'qrcode.react';

interface VisitorAppSdc {
  sdcUuid: string;
  sdcSerialNumber: string;
  doorPlace: string;
  autoBuildingId: number;
  buildingName: string;
  buildingAddress: string;
}

interface VisitorApp {
  autoVisitorId: number;
  isEnabled: boolean;
  activateId: string;
  activatePassword: string;
  activatedAt: string | null;
  authToken: string | null;
  extension: string | null;
  memo: string;
  sdc: VisitorAppSdc | null;
  eep?: string;
}

interface Change {
  isEnabled: boolean;
  memo: string;
}

interface MakeCallModalProps {
  authToken: string | null;
  setClose: () => void;
  showSuccessMessage: (message: string) => void;
  showErrorMessage: (message: string) => void;
}

const MakeCallModal: React.FC<MakeCallModalProps> = (
  props: MakeCallModalProps
) => {
  const [roomNumber, setRoomNumber] = useState<string>('');
  const [group, setGroup] = useState<number>(0);
  const [isSubmitInProgress, setIsSubmitInProgress] = useState<boolean>(false);

  const handleSubmit = () => {
    setIsSubmitInProgress(true);
    fetch(process.env.REACT_APP_API_ENDPOINT + `/icm/v1/calls`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: props.authToken!,
      },
      body: JSON.stringify({
        roomNumber: roomNumber,
        group: group,
        thumbnailData: null,
        sessionId: null,
      }),
    })
      .then((response) => {
        if (response.status !== 200) {
          console.error(response.status);
          props.showErrorMessage('APIの呼び出しに失敗しました。');
          return null;
        }
        return response.json();
      })
      .then((body) => {
        console.log(body);
        props.showSuccessMessage('指定された部屋番号に呼び出しをかけました。');
      })
      .catch((e) => {
        console.error(e);
        props.showErrorMessage('APIの呼び出しに失敗しました。');
      })
      .finally(() => {
        setIsSubmitInProgress(false);
        props.setClose();
      });
  };

  return (
    <Dialog
      open={props.authToken !== null}
      onClose={() => {
        props.setClose();
      }}
      aria-labelledby="form-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="form-dialog-title">呼び出しテスト</DialogTitle>
      <DialogContent sx={{ px: 5 }}>
        <Box sx={{ display: 'flex', w: 'sm', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ textAlign: 'right', mr: 1 }}>部屋番号</Typography>
          <TextField
            size="small"
            defaultValue={roomNumber}
            onBlur={(e) => {
              setRoomNumber(e.target.value);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', w: 'sm', alignItems: 'center' }}>
          <Typography sx={{ textAlign: 'right', mr: 1 }}>グループ</Typography>
          <Select
            size="small"
            value={group}
            onChange={(e) => {
              setGroup(e.target.value as number);
            }}
          >
            <MenuItem value={0}>0</MenuItem>
            <MenuItem value={1}>1</MenuItem>
            <MenuItem value={2}>2</MenuItem>
          </Select>
        </Box>
      </DialogContent>
      <DialogActions sx={{ m: 1 }}>
        <Button color="primary" variant="contained" onClick={handleSubmit}>
          {isSubmitInProgress ? (
            <CircularProgress size={20} sx={{ color: 'white' }} />
          ) : (
            '送信'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const VisitorAppListPage = () => {
  const [baseVisitors, setBaseVisitors] = useState<VisitorApp[]>([]);
  const [displayVisitors, setDisplayVisitors] = useState<VisitorApp[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [saveInProgressId, setSaveInProgressId] = useState<number>(0);
  const [changes, setChanges] = useState<Map<number, Change>>(
    new Map<number, Change>()
  );

  // 'deleteId' indicates ID of deletion target row; 0 if none
  // this keeps the value through opening and closing confirm dialog
  const [deleteId, setDeleteId] = useState<number>(0);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

  const [makeCallWithAuthToken, setMakeCallWithAuthToken] = useState<
    string | null
  >(null);

  const [openModal, setOpenModal] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>('success');

  const [showQrCodeOfVisitorId, setShowQrCodeOfVisitorId] = useState<number>(0);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const showErrorMessage = (message: string) => {
    setSnackbarMessage(message);
    setAlertSeverity('error');
    setSnackbarOpen(true);
  };

  const showSuccessMessage = (message: string) => {
    setSnackbarMessage(message);
    setAlertSeverity('success');
    setSnackbarOpen(true);
  };

  const onRender = () => {
    setIsLoading(true);
    getJwtTokenFromIdToken().then((token) => {
      fetch(`${process.env.REACT_APP_API_ENDPOINT}/visitors`, {
        headers: {
          Authorization: token,
        },
      })
        .then((response) => {
          if (response.status !== 200) {
            console.error(response.status);
            showErrorMessage(
              '訪問者端末一覧の取得に失敗しました。サーバ管理者に問い合わせてください。'
            );
            return null;
          }
          return response.json();
        })
        .then((body) => {
          if (body !== null) {
            setBaseVisitors(body.visitors);
            setDisplayVisitors(body.visitors);
          }
        })
        .catch((e) => {
          console.error(e);
          showErrorMessage(
            '訪問者端末一覧の取得に失敗しました。サーバ管理者に問い合わせてください。'
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    });
  };

  const onClickSaveRow = (id: number) => {
    setSaveInProgressId(id);
    getJwtTokenFromIdToken().then((token) => {
      // update target visitor
      const updateData = changes.get(id);
      if (updateData === undefined) {
        console.error('This must not happen!');
        return;
      }
      fetch(process.env.REACT_APP_API_ENDPOINT + `/visitors/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          isEnabled: updateData.isEnabled,
          memo: updateData.memo,
        }),
      })
        .then((response) => {
          if (response.status !== 200) {
            console.error(response.status);
            showErrorMessage(
              '訪問者端末の更新に失敗しました。サーバ管理者に問い合わせてください。'
            );
            return null;
          }
          return response.json();
        })
        .then((body) => {
          if (body !== null) {
            // update the target row; displayVisitors need not to be
            // updated
            setBaseVisitors(
              baseVisitors.map((visitor) => {
                if (visitor.autoVisitorId === id) {
                  return body;
                }
                return visitor;
              })
            );
            // delete change
            const tmpChanges = new Map(changes);
            tmpChanges.delete(id);
            setChanges(tmpChanges);

            showSuccessMessage('訪問者端末の更新に成功しました。');
          }
        })
        .catch((e) => {
          console.error(e);
          showErrorMessage(
            '訪問者端末の更新に失敗しました。サーバ管理者に問い合わせてください。'
          );
        })
        .finally(() => {
          setSaveInProgressId(0);
        });
    });
  };

  const onConfirmDelete = () => {
    if (deleteId === 0) {
      return;
    }
    getJwtTokenFromIdToken().then((token) => {
      // delete target visitor
      fetch(process.env.REACT_APP_API_ENDPOINT + `/visitors/${deleteId}`, {
        method: 'DELETE',
        headers: {
          Authorization: token,
        },
      })
        .then((response) => {
          if (response.status !== 200) {
            console.error(response.status);
            showErrorMessage(
              '訪問者端末の削除に失敗しました。サーバ管理者に問い合わせてください。'
            );
            return null;
          }
          return response.json();
        })
        .then((body) => {
          if (body !== null) {
            // delete the target row
            setBaseVisitors(
              baseVisitors.filter((visitor) => {
                return visitor.autoVisitorId !== deleteId;
              })
            );
            setDisplayVisitors(
              displayVisitors.filter((visitor) => {
                return visitor.autoVisitorId !== deleteId;
              })
            );
            // delete change
            const tmpChanges = new Map(changes);
            tmpChanges.delete(deleteId);
            setChanges(tmpChanges);

            showSuccessMessage('訪問者端末の削除に成功しました。');
          }
        })
        .catch((e) => {
          console.error(e);
          showErrorMessage(
            '訪問者端末の削除に失敗しました。サーバ管理者に問い合わせてください。'
          );
        })
        .finally(() => {
          setDeleteId(0);
        });
    });
  };

  const initChange = (visitor: VisitorApp): Change => {
    return {
      isEnabled: visitor.isEnabled,
      memo: visitor.memo,
    };
  };

  const isVisitorChanged = (visitor: VisitorApp, change: Change): boolean => {
    return !(
      visitor.isEnabled === change.isEnabled && visitor.memo === change.memo
    );
  };

  const handleChangeMade = (
    id: number,
    key: 'isEnabled' | 'memo',
    value: any
  ) => {
    let targetBaseVisitor: VisitorApp | null = null;
    for (let i = 0; i < baseVisitors.length; i++) {
      if (baseVisitors[i].autoVisitorId === id) {
        targetBaseVisitor = baseVisitors[i];
        break;
      }
    }

    const existingChange = changes.get(id);
    const tmpChange = {
      ...(existingChange ?? initChange(targetBaseVisitor!)),
    };
    switch (key) {
      // type assertion and assignment to displayed data
      case 'isEnabled':
        tmpChange[key] = value as boolean;
        setDisplayVisitors(
          displayVisitors.map((visitor) => {
            if (visitor.autoVisitorId === id) {
              return {
                ...visitor,
                isEnabled: value as boolean,
              };
            }
            return visitor;
          })
        );
        break;
      case 'memo':
        tmpChange[key] = value as string;
        setDisplayVisitors(
          displayVisitors.map((visitor) => {
            if (visitor.autoVisitorId === id) {
              return {
                ...visitor,
                memo: value as string,
              };
            }
            return visitor;
          })
        );
        break;
    }

    const tmpChanges = new Map(changes);

    if (existingChange !== undefined) {
      if (!isVisitorChanged(targetBaseVisitor!, tmpChange)) {
        // all changes set to original value; remove from map
        tmpChanges.delete(id);
        setChanges(tmpChanges);
      } else {
        // some of other values stay changed; update map
        tmpChanges.set(id, tmpChange);
        setChanges(tmpChanges);
      }
    } else {
      // it's the first time change has made on this visitor; register
      // to map
      tmpChanges.set(id, tmpChange);
      setChanges(tmpChanges);
    }
  };

  const QrCodeDialog = () => {
    const targetVisitor = baseVisitors.find((v) => {
      return v.autoVisitorId === showQrCodeOfVisitorId;
    });
    return (
      <Dialog
        open={targetVisitor !== undefined}
        onClose={() => {
          setShowQrCodeOfVisitorId(0);
        }}
      >
        {targetVisitor !== undefined && (
          <Box sx={{ p: 7 }}>
            <QRCode
              value={JSON.stringify({
                id: targetVisitor.activateId,
                password: targetVisitor.activatePassword,
                ...(targetVisitor.eep && targetVisitor.eep.trim() !== "" && { eep: targetVisitor.eep })
              })}
              size={320}
              renderAs="svg"
            />
          </Box>
        )}
      </Dialog>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'activateId',
      headerName: 'アクティベートID',
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'activatePassword',
      headerName: 'アクティベートパスワード',
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'activatedAt',
      headerName: 'アクティベート日時',
      flex: 1,
      valueFormatter: (params: any) => {
        if (params.value) {
          const dateObj = new Date(params.value);
          return dateObj.toLocaleDateString('ja-JP');
        }
        return '';
      },
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'extension',
      headerName: '内線番号',
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'sdcSerialNumber',
      headerName: 'SDCSN',
      flex: 1,
      valueGetter: (params) => params.row.sdc?.sdcSerialNumber || '',
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'buildingName',
      headerName: '物件名称',
      flex: 1,
      valueGetter: (params: any) => params.row.sdc?.buildingName || '',
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'buildingAddress',
      headerName: '設置物件住所',
      flex: 1,
      valueGetter: (params: any) => params.row.sdc?.buildingAddress || '',
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'doorPlace',
      headerName: 'ドア設置場所',
      flex: 1,
      valueGetter: (params) => params.row.sdc?.doorPlace || '',
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'isEnabled',
      headerName: '有効・無効',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <FormControl component="fieldset">
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={params.value}
                  onChange={(e) => {
                    handleChangeMade(
                      params.row.autoVisitorId,
                      'isEnabled',
                      e.target.checked
                    );
                  }}
                />
              }
              label={params.value ? '有効' : '無効'}
            />
          </FormGroup>
        </FormControl>
      ),
    },
    {
      field: 'memo',
      headerName: 'メモ',
      flex: 2,
      renderCell: (params: GridRenderCellParams) => (
        <TextField
          size="small"
          defaultValue={params.value}
          onBlur={(e) => {
            handleChangeMade(params.row.autoVisitorId, 'memo', e.target.value);
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
        />
      ),
    },
    {
      field: 'autoVisitorId',
      headerName: '操作',
      flex: 3,
      renderCell: (params: GridRenderCellParams) => (
        <>
          <Tooltip title="QRコード表示">
            <IconButton
              size="small"
              onClick={() => {
                setShowQrCodeOfVisitorId(params.row.autoVisitorId);
              }}
              sx={{ mr: 1 }}
            >
              <QrCode2Icon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => {
              setMakeCallWithAuthToken(params.row.authToken);
            }}
            sx={{ mr: 1 }}
            disabled={params.row.authToken === null}
          >
            呼び出しテスト
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => {
              setDeleteId(params.row.autoVisitorId);
              setOpenDeleteConfirm(true);
            }}
            sx={{ mr: 1 }}
          >
            {deleteId === params.row.autoVisitorId ? (
              <CircularProgress size={15} sx={{ m: 0.5, color: 'white' }} />
            ) : (
              '削除'
            )}
          </Button>
          {changes.get(params.value) && (
            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={() => {
                onClickSaveRow(params.row.autoVisitorId);
              }}
            >
              {saveInProgressId === params.row.autoVisitorId ? (
                <CircularProgress size={15} sx={{ m: 0.5, color: 'white' }} />
              ) : (
                '保存'
              )}
            </Button>
          )}
        </>
      ),
    },
  ];

  useEffect(() => {
    // fetch visitors once
    onRender();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CustomContainer maxWidth={false}>
      <Box display="flex" flexDirection="column" marginBottom={2}>
        <Typography variant="h5" component="h2" gutterBottom>
          登録済み訪問者端末一覧
        </Typography>
        <Box display="flex" justifyContent="flex-end" sx={{ mb: 0.5 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setOpenModal(true)}
          >
            新規登録
          </Button>
          <Box marginLeft={1}>
            <UpdateButton />
          </Box>
        </Box>
        <Box display="flex" justifyContent="flex-end" sx={{ minHeight: 25 }}>
          <Typography color="orangered">
            {changes.size > 0 ? '保存されていない変更があります' : ''}
          </Typography>
        </Box>
      </Box>
      <div style={{ height: 700, width: '100%' }}>
        <StyledDataGrid
          rows={displayVisitors}
          columns={columns}
          getRowId={(row) => row.autoVisitorId}
          disableRowSelectionOnClick
          localeText={
            isLoading
              ? customDataGridLocaleTextLoading
              : displayVisitors.length === 0
              ? customDataGridLocaleTextNoData
              : {}
          }
        />
      </div>

      <QrCodeDialog />

      <VisitorAppRegistrationModal
        open={openModal}
        setOpen={setOpenModal}
        setSnackbarOpen={setSnackbarOpen}
        setAlertSeverity={setAlertSeverity}
        setSnackbarMessage={setSnackbarMessage}
      />

      <MakeCallModal
        authToken={makeCallWithAuthToken}
        setClose={() => {
          setMakeCallWithAuthToken(null);
        }}
        showSuccessMessage={showSuccessMessage}
        showErrorMessage={showErrorMessage}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={alertSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={openDeleteConfirm}
        onClose={() => {
          setOpenDeleteConfirm(false);
        }}
        onConfirm={async () => {
          onConfirmDelete();
        }}
        title="確認"
        description="本当に削除しますか？"
      />
    </CustomContainer>
  );
};

export default VisitorAppListPage;
